import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnInit, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { User } from '../../../app/administration/users/user.model';
import { UsersService } from '../../../app/administration/users/users.service';
import { environment } from '../../../environments/environment';
import { mpsRouteNames } from '../../mps-route-names';
import { MpsNotificationService } from '../../services/mps-notification.service';
import { MpsUserService } from '../../services/mps-user.service';
import { MpsNavigationComponent } from '../mps-navigation/mps-navigation.component';

@Component({
  selector: 'mps-app-header',
  templateUrl: './mps-header.component.html',
  styleUrls: ['./mps-header.component.scss'],
  imports: [
    TranslateModule,
    MatMenuModule,
    MatIconModule,
    MpsNavigationComponent,
    CommonModule,
    RouterModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
})
export class MpsHeaderComponent implements OnInit {
  @Input() public nav?: MatSidenav;
  mpsUserService = inject(MpsUserService);
  _notificationService = inject(MpsNotificationService);
  translateService = inject(TranslateService);
  private _eRef = inject(ElementRef);
  user = signal(this.mpsUserService.getUserSubject().getValue());
  isDropdownOpen = signal(false);
  logoutRoute = mpsRouteNames.auth + '/' + mpsRouteNames.logout;
  readonly canImpersonate = environment.allowImpersonation;
  private _usersService = inject(UsersService);
  search = new FormControl();
  usersToImpersonate = signal<User[]>([]);

  ngOnInit(): void {
    this.search.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((searchInput) => searchInput.length >= 3)
      )
      .subscribe((searchInput) => {
        this._usersService.usersToImpersonate(searchInput).subscribe({
          next: (users) => {
            this.usersToImpersonate.set(users);
          },
          error: () => {
            this.usersToImpersonate.set([]);
            this._notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
          },
        });
      });
  }

  getCurrentLangLabel(): string {
    return this.getLangLabel(this.translateService.currentLang);
  }

  getLangLabel(lang: string): string {
    const labels: Record<string, string> = {
      fr: 'Français',
      en: 'English',
    };
    return labels[lang] || this.translateService.instant('general.missing_translation');
  }

  toggleDropdown() {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  switchLanguage(language: string) {
    this.translateService.use(language);
    const user = this.mpsUserService.getUser();
    localStorage.setItem('lang', language);
    if (user) {
      this.mpsUserService.changeLang(user.id, language)?.subscribe();
    }
  }

  getUserDisplayName() {
    return this.user()?.firstname + ' ' + this.user()?.lastname.substring(0, 1) + '.';
  }

  /**
   * Environment badge shown next to the username: "env local" on local,
   * "Test environment" on the dev and release (preprod) builds, empty string on
   * production (no badge). Lets users tell at a glance which environment they
   * are on.
   */
  getEnvLabel(): string {
    if (environment.production) {
      return '';
    }
    return environment.envName === 'local' ? 'env local' : 'Test environment';
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (!this._eRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
    }
  }

  displayUser(user?: User | null): string {
    return user ? `${user.igg} - ${user.firstname} ${user.lastname} | ${user.role.label}` : '';
  }

  impersonate(user: User): void {
    this._usersService.impersonate(user.id).subscribe({
      next: (token: string) => {
        localStorage.setItem('token', token);
        window.location.reload();
      },
      error: () => {
        this._notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
      },
    });
  }
}
